import {api} from "@/api"
import {router} from "@/router"
import {popupEpki} from "@/utils/epki"
import {computed} from "@vue/reactivity"
import {defineStore} from "pinia"
import {ref, watch, onScopeDispose} from "vue"
import {clearUserSession, readUserSession, writeUserSession, USER_SESSION_STORAGE_KEY} from "./userSessionStorage"
import {clearTemporaryDocuments, syncTemporaryDocumentOwner, handleDocumentSessionChange} from "@/utils/temporaryDocuments"

export const useUserStore = defineStore("user", () => {
    const storedUserSession = readUserSession()
    const user = ref(storedUserSession?.user)
    const accessToken = ref(storedUserSession?.accessToken || "")
    const refreshToken = ref(storedUserSession?.refreshToken || "")
    const tokenType = ref(storedUserSession?.tokenType || "")
    const logoutAt = ref(storedUserSession?.logoutAt || 0)
    const epkiInfo = ref()
    const phoneInfo = ref()
    const snsInfo = ref()

    const userId = computed(() => user.value?.userId || "")
    watch(userId, syncTemporaryDocumentOwner, { immediate: true, flush: "sync" })
    window.addEventListener("storage", handleDocumentSessionChange)
    window.addEventListener("storage", handleSessionChange)
    window.addEventListener("focus", syncUserSession)
    document.addEventListener("visibilitychange", syncUserSession)
    onScopeDispose(() => {
        window.removeEventListener("storage", handleDocumentSessionChange)
        window.removeEventListener("storage", handleSessionChange)
        window.removeEventListener("focus", syncUserSession)
        document.removeEventListener("visibilitychange", syncUserSession)
    })
    const scholId = computed(() => user.value?.schoolId || "")
    const userEmail = computed(() => user.value?.email)
    const isLoggedIn = computed(() => !!user.value)
    const isStudent = computed(() => user.value?.group === "01")
    const isParent = computed(() => user.value?.group === "02")
    const isTeacher = computed(() => user.value?.group === "03")
    const isAdmin = computed(() => user.value?.group === "99")

    function syncUserSession() {
        const session = readUserSession()
        // 이벤트에 담긴 과거 값 대신 공통 저장소의 최신 상태를 사용한다.
        if (JSON.stringify(user.value) !== JSON.stringify(session?.user)) user.value = session?.user
        accessToken.value = session?.accessToken || ""
        refreshToken.value = session?.refreshToken || ""
        tokenType.value = session?.tokenType || ""
        logoutAt.value = session?.logoutAt || 0
    }

    function handleSessionChange(event) {
        if (event.storageArea === localStorage && (event.key === null || event.key === USER_SESSION_STORAGE_KEY)) {
            syncUserSession()
        }
    }

    function extendSession() {
        syncUserSession()
        if (!accessToken.value) return
        logoutAt.value = Date.now() + 60 * 60 * 1000
        writeUserSession({ user: user.value, accessToken: accessToken.value,
            refreshToken: refreshToken.value, tokenType: tokenType.value, logoutAt: logoutAt.value })
    }

    function ensureSessionDeadline() {
        syncUserSession()
        if (accessToken.value && !logoutAt.value) extendSession()
    }

    function setLoginUser(payload) {
        user.value = payload.loginUser.user
        accessToken.value = payload.accessToken
        refreshToken.value = payload.refreshToken
        tokenType.value = payload.tokenType
        logoutAt.value = Date.now() + 60 * 60 * 1000
        writeUserSession({ ...payload, logoutAt: logoutAt.value })
    }

    function setLogout() {
        clearTemporaryDocuments()
        user.value = undefined
        accessToken.value = ""
        refreshToken.value = ""
        tokenType.value = ""
        logoutAt.value = 0
        clearUserSession()
    }

    function clearAuthState() {
        setLogout()
    }

    async function checkTeacher(email) {
        const result = await api.login.checkTeacher({email})

        if (!result.data.success) return
        if (result.data.errorCode !== "01" && result.data.errorCode !== "02") return

        const message = result.data.errorCode === "01" ? "교직원 인증서가 존재하지 않습니다.\nPC에서 인증서를 등록 하세요." : "교직원 인증서가 만료 되었습니다.\nPC에서 인증서를 재등록 하세요."

        alert(message)

        const params = await popupEpki({isLogin: true})

        if (!params.success) throw new Error("인증서 확인에 실패하였습니다.")

        return params
    }

    function setEpkiInfo(info) {
        epkiInfo.value = info
    }

    function setPhoneInfo(info) {
        phoneInfo.value = info
    }

    function setSnsInfo(info) {
        snsInfo.value = info
    }

    // async function login(email, password, next = "/main") {
    //   if (email) {
    //     const params = await checkTeacher(email)
    //     setEpkiInfo(params)
    //   }

    //   const result = await api.login.login({
    //     email,
    //     password,
    //     isSnsLogin: snsInfo.value?.isSnsLogin,
    //     appId: snsInfo.value?.appId,
    //     snsType: snsInfo.value?.snsType,
    //   })

    //   if (result.data.success) {
    //     setLoginUser(result.data)
    //     setSnsInfo(null)
    //     setPhoneInfo(null)
    //     router.push(next)
    //   } else {
    //     alert(
    //       "아이디(이메일) 또는 비밀번호를 잘못 입력했습니다.\n입력하신 내용을 다시 확인해주세요.",
    //     )
    //   }
    // }

    // async function loginTeacher(email, password, next = "/main") {
    //     // if (email) {
    //     //   const params = await checkTeacher(email)
    //     //   setEpkiInfo(params)
    //     // }
    //     const result = await api.teacher.login({
    //         email, password,
    //     })
    //
    //     if (result.data.success) {
    //         setLoginUser(result.data)
    //         router.push(next)
    //     } else {
    //         alert("아이디(이메일) 또는 비밀번호를 잘못 입력했습니다.\n입력하신 내용을 다시 확인해주세요.",)
    //     }
    // }

    // async function loginTeacher(email, password, next = "/main") {
    //     const MAX_ATTEMPTS = 5;
    //     const WAIT_TIME_MS = 2 * 60 * 1000; // 2분
    //
    //     //epki 로그인시 사용
    //     // if (email) {
    //     //   const params = await checkTeacher(email)
    //     //   setEpkiInfo(params)
    //     // }
    //
    //     // 현재 시각
    //     const now = new Date().getTime();
    //
    //     // 세션스토리지에서 로그인 시도 정보 가져오기
    //     let loginAttempts = JSON.parse(sessionStorage.getItem('teacherLoginAttempts')) || { count: 0, timestamp: now };
    //
    //     // 만약 시도 횟수가 최대 시도 횟수를 초과했으면
    //     if (loginAttempts.count >= MAX_ATTEMPTS) {
    //         // 대기 시간이 지나지 않았다면 경고 및 종료
    //         if (now - loginAttempts.timestamp < WAIT_TIME_MS) {
    //             alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
    //             return;
    //         } else {
    //             // 대기 시간이 지나면 시도 횟수 초기화
    //             loginAttempts = { count: 0, timestamp: now };
    //         }
    //     }
    //
    //     let result = null;
    //
    //     try {
    //         result = await api.teacher.login({ email, password });
    //         console.log(result); // 결과 로그 출력하여 확인
    //
    //         if (result.data.success) {
    //             setLoginUser(result.data);
    //             router.push(next);
    //             // 성공하면 시도 횟수 초기화
    //             sessionStorage.removeItem('teacherLoginAttempts');
    //         }
    //
    //     } catch (error) {
    //         console.error('API 호출 중 오류 발생:', error);
    //         // 실패하면 시도 횟수 증가
    //         loginAttempts.count += 1;
    //         loginAttempts.timestamp = now;
    //         sessionStorage.setItem('teacherLoginAttempts', JSON.stringify(loginAttempts));
    //
    //         if (loginAttempts.count >= MAX_ATTEMPTS) {
    //             alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
    //         } else {
    //             alert("아이디(이메일) 또는 비밀번호를 잘못 입력했습니다.\n입력하신 내용을 다시 확인해주세요.");
    //         }
    //     }
    // }
    //
    //
    // async function loginStudent(email, password, next = "/main") {
    //     const MAX_ATTEMPTS = 5;
    //     const WAIT_TIME_MS = 2 * 60 * 1000; // 2분
    //
    //     // 현재 시각
    //     const now = new Date().getTime();
    //
    //     // 로컬스토리지에서 로그인 시도 정보 가져오기
    //     let loginAttempts = JSON.parse(sessionStorage.getItem('loginAttempts')) || {count: 0, timestamp: now};
    //
    //     // 만약 시도 횟수가 최대 시도 횟수를 초과했으면
    //     if (loginAttempts.count >= MAX_ATTEMPTS) {
    //         // 대기 시간이 지나지 않았다면 경고 및 종료
    //         if (now - loginAttempts.timestamp < WAIT_TIME_MS) {
    //             alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
    //             return;
    //         } else {
    //             // 대기 시간이 지나면 시도 횟수 초기화
    //             loginAttempts = {count: 0, timestamp: now};
    //         }
    //     }
    //
    //     let result = null;
    //
    //     try {
    //         result = await api.student.login({ email, password });
    //         console.log(result); // 결과 로그 출력하여 확인
    //
    //         if (result.data.success) {
    //             setLoginUser(result.data);
    //             router.push(next);
    //             // 성공하면 시도 횟수 초기화
    //             sessionStorage.removeItem('loginAttempts');
    //         }
    //
    //     } catch (error) {
    //         console.error('API 호출 중 오류 발생:', error);
    //         // 실패하면 시도 횟수 증가
    //         loginAttempts.count += 1;
    //         loginAttempts.timestamp = now;
    //         sessionStorage.setItem('loginAttempts', JSON.stringify(loginAttempts));
    //
    //         if (loginAttempts.count >= MAX_ATTEMPTS) {
    //             alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
    //         } else {
    //             console.log(error)
    //             alert(error.response.data.message);
    //         }
    //     }
    // }

    //교사 로그인
    async function loginTeacher(email, password, next = "/main") {
        //epki 로그인시 사용
        // if (email) {
        //   const params = await checkTeacher(email)
        //   setEpkiInfo(params)
        // }

        await login(email, password, next, 'teacher', api.teacher.login, 'teacherLoginAttempts');
    }

    //학생 로그인
    async function loginStudent(email, password, next = "/main") {
        await login(email, password, next, 'student', api.student.login, 'studentLoginAttempts');
    }

    async function login(email, password, next, userType, apiLoginMethod, storageKey) {
        const MAX_ATTEMPTS = 4;
        const WAIT_TIME_MS = 2 * 60 * 1000; // 2분
        const skipAttemptLimit = import.meta.env.DEV
            || ['localhost', '127.0.0.1', '[::1]', 'jinhakai.donggyu.link'].includes(window.location.hostname);

        if (skipAttemptLimit) {
            sessionStorage.removeItem(storageKey);
        }

        // 현재 시각
        const now = new Date().getTime();

        // 세션스토리지에서 로그인 시도 정보 가져오기
        let loginAttempts = (!skipAttemptLimit && JSON.parse(sessionStorage.getItem(storageKey))) || { count: 0, timestamp: now };

        // 만약 시도 횟수가 최대 시도 횟수를 초과했으면
        if (loginAttempts.count >= MAX_ATTEMPTS) {
            // 대기 시간이 지나지 않았다면 경고 및 종료
            if (now - loginAttempts.timestamp < WAIT_TIME_MS) {
                alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
                return;
            } else {
                // 대기 시간이 지나면 시도 횟수 초기화
                loginAttempts = { count: 0, timestamp: now };
            }
        }

        let result = null;

        try {
            result = await apiLoginMethod({ email, password });
            console.log(result); // 결과 로그 출력하여 확인

            if (result.data.success) {
                setLoginUser(result.data);
                router.push(next);
                // 성공하면 시도 횟수 초기화
                sessionStorage.removeItem(storageKey);
            }
        } catch (error) {
            console.error('API 호출 중 오류 발생:', error);
            if (skipAttemptLimit) {
                alert(error.response.data.message);
                return;
            }
            // 실패하면 시도 횟수 증가
            loginAttempts.count += 1;
            loginAttempts.timestamp = now;
            sessionStorage.setItem(storageKey, JSON.stringify(loginAttempts));

            if (loginAttempts.count >= MAX_ATTEMPTS) {
                alert("로그인 시도가 너무 많습니다. 2분 후에 다시 시도해 주세요.");
            } else {
                alert(error.response.data.message);
            }
        }
    }



    // async function loginStudent(email, password, next = "/main") {
    //   const result = await api.student.login({
    //     email,
    //     password,
    //   })
    //
    //   if (result.data.success) {
    //     setLoginUser(result.data)
    //     router.push(next)
    //   } else {
    //     alert(
    //       "아이디(이메일) 또는 비밀번호를 잘못 입력했습니다.\n입력하신 내용을 다시 확인해주세요.",
    //     )
    //   }
    // }

    async function logout() {
        syncUserSession()
        const requestToken = accessToken.value
        if (!requestToken) return
        const result = await api.login.logout({email: userEmail.value})

        syncUserSession()
        if (accessToken.value !== requestToken) return

        if (result.data.success) {
            clearAuthState()
            router.push("/main")
        } else {
            alert(result.data.message || "로그아웃에 실패하였습니다.")
        }
    }

    async function setImpersonationToken(payload) {
        // 토큰 설정
        accessToken.value = payload.accessToken
        tokenType.value = payload.tokenType
        writeUserSession({
            user: user.value,
            accessToken: payload.accessToken,
            refreshToken: refreshToken.value,
            tokenType: payload.tokenType,
            logoutAt: logoutAt.value,
        })

        // 사용자 정보 다시 조회
        try {
            const userInfo = await api.login.userInfo()
            if (userInfo.data.success) {
                user.value = userInfo.data.loginUser.user
                writeUserSession({
                    user: user.value,
                    accessToken: payload.accessToken,
                    refreshToken: refreshToken.value,
                    tokenType: payload.tokenType,
                    logoutAt: logoutAt.value,
                })
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error)
        }
    }

    return {
        epkiInfo,
        phoneInfo,
        snsInfo,
        user,
        tokenType,
        accessToken,
        refreshToken,
        logoutAt,
        syncUserSession,
        extendSession,
        ensureSessionDeadline,
        userId,
        scholId,
        isAdmin,
        isLoggedIn,
        isStudent,
        isParent,
        isTeacher,
        loginTeacher,
        loginStudent,
        logout,
        clearAuthState,
        setEpkiInfo,
        setPhoneInfo,
        setSnsInfo,
        setImpersonationToken,
    }
}, {
    persist: {
        storage: sessionStorage,
        paths: ["epkiInfo", "phoneInfo", "snsInfo"],
        // 이전 버전이 탭에 저장한 인증 상태도 복원하지 않는다.
        serializer: {
            serialize: JSON.stringify,
            deserialize(value) {
                const { epkiInfo, phoneInfo, snsInfo } = JSON.parse(value)
                return { epkiInfo, phoneInfo, snsInfo }
            },
        },
    },
},)
